/**
 * State restoration for speech bubbles
 * Handles restoring bubble state when loading a saved game
 */

import { scrollManager } from '../../../utils/scroll'
import {
  createBubbleImage,
  animateBubblePopOut,
  setupBubbleClickAndHover,
  getBubbleImageForActionPoint,
  getBubbleIdForActionPoint,
} from './bubble-manager'
import { createActionPoint } from './action-points'
import type { SceneData, SceneConfig, ChoiceCondition } from '../types'
import { filterBubblesByCondition } from './bubble-filter'

export interface StateRestorationContext {
  data: SceneData
  config: SceneConfig
  imageContainer: HTMLDivElement | null
  clickedActionPointIndices: Set<number>
  actionPoints: HTMLDivElement[]
  currentActionPointIndex: number
  unlockedBubbleIds: Set<string>
  onCreateActionPoint: (xPercent: number, yPercent: number, bubbleIndex: number, actionPointIndex: number) => void
  onShowChoices?: () => void
  onEvaluateCondition?: (condition: ChoiceCondition) => boolean
}

/**
 * Restore bubble state from saved game
 */
export function restoreBubbleState(
  lastBubbleId: string | null,
  lastActionPointIndex: number | null,
  context: StateRestorationContext
): number {
  if (lastActionPointIndex === null && lastBubbleId === null) return -1

  // Filter bubbles based on conditions
  const filteredBubbles = filterBubblesByCondition(
    context.data.speechBubbles,
    context.onEvaluateCondition
  )
  if (filteredBubbles.length === 0) return -1

  // If we have an action point index, restore all bubbles up to that point
  // Note: lastActionPointIndex refers to the filtered array
  if (lastActionPointIndex !== null && lastActionPointIndex < filteredBubbles.length) {
    // Mark all action points up to and including the last one as clicked
    for (let i = 0; i <= lastActionPointIndex; i++) {
      context.clickedActionPointIndices.add(i)

      // Show the bubble image for this action point
      const bubble = filteredBubbles[i]
      // Find original index for getBubbleImageForActionPoint
      const originalIndex = context.data.speechBubbles?.findIndex((b) => b.id === bubble.id) ?? i
      const bubbleImageData = getBubbleImageForActionPoint(context.data, originalIndex)

      if (bubble && bubbleImageData && context.imageContainer) {
        // Use bubble.position for bubble image, not actionPoint.position
        const bubbleImg = createBubbleImage(
          bubbleImageData.image,
          bubble.position.x,
          bubble.position.y,
          bubbleImageData.width,
          i,
        )
        context.imageContainer.appendChild(bubbleImg)
        // Use bubble.id directly if available, otherwise derive from filtered index
        const bubbleId = bubble.id || `bubble-${i + 1}`
        context.unlockedBubbleIds.add(bubbleId)
        // Animate bubble pop-out
        animateBubblePopOut(
          bubbleImg,
          i,
          context.config,
          (bubbleImg, bubbleIndex) => {
            setupBubbleClickAndHover(bubbleImg, bubbleIndex, context.data)
          }
        )
      }
    }
  } else if (lastBubbleId) {
    // If we only have a bubble ID, find it in the filtered array and restore up to that bubble
    const bubbleIndexInFiltered = filteredBubbles.findIndex((b) => {
      const bubbleId = b.id || `bubble-${filteredBubbles.indexOf(b) + 1}`
      return bubbleId === lastBubbleId
    })

    if (bubbleIndexInFiltered >= 0) {
      for (let i = 0; i <= bubbleIndexInFiltered; i++) {
        context.clickedActionPointIndices.add(i)

        const bubble = filteredBubbles[i]
        // Find original index for getBubbleImageForActionPoint
        const originalIndex = context.data.speechBubbles?.findIndex((b) => b.id === bubble.id) ?? i
        const bubbleImageData = getBubbleImageForActionPoint(context.data, originalIndex)

        if (bubble && bubbleImageData && context.imageContainer) {
          const bubbleImg = createBubbleImage(
            bubbleImageData.image,
            bubble.position.x,
            bubble.position.y,
            bubbleImageData.width,
            i,
          )
          context.imageContainer.appendChild(bubbleImg)
          context.unlockedBubbleIds.add(getBubbleIdForActionPoint(context.data, i))
          animateBubblePopOut(
            bubbleImg,
            i,
            context.config,
            (bubbleImg, bubbleIndex) => {
              setupBubbleClickAndHover(bubbleImg, bubbleIndex, context.data)
            }
          )
        }
      }
    }
  }

  // Remove any action points from DOM (they should not have been created if state was checked properly)
  context.actionPoints.forEach((ap) => ap.remove())
  context.actionPoints = []

  // Determine the actual last action point index that was restored (in filtered array)
  const actualLastIndex =
    lastActionPointIndex !== null
      ? lastActionPointIndex
      : lastBubbleId
        ? (() => {
            const foundIndex = filteredBubbles.findIndex((b) => {
              const bubbleId = b.id || `bubble-${filteredBubbles.indexOf(b) + 1}`
              return bubbleId === lastBubbleId
            })
            return foundIndex
          })()
        : -1

  // Show next action point if there is one
  if (actualLastIndex >= 0 && actualLastIndex < filteredBubbles.length - 1) {
    context.currentActionPointIndex = actualLastIndex + 1
    const nextBubble = filteredBubbles[context.currentActionPointIndex]
    if (!nextBubble.actionPoint || !nextBubble.actionPoint.position) {
      console.error(
        `Scene ${context.data.id}: speechBubble "${nextBubble.id}" is missing required "actionPoint.position"`,
      )
      return actualLastIndex
    }
    const actionPointPos = nextBubble.actionPoint.position
    context.onCreateActionPoint(
      actionPointPos.x,
      actionPointPos.y,
      context.currentActionPointIndex,
      context.currentActionPointIndex,
    )
    context.currentActionPointIndex++
  }

  // Unlock scroll if all action points were clicked
  if (actualLastIndex >= 0 && actualLastIndex >= filteredBubbles.length - 1) {
    scrollManager.removeMaxScroll()
  }

  return actualLastIndex
}
